import React, { useEffect, useMemo, useState } from 'react';
import BlogPostCard from './BlogPostCard';
import { blogPosts } from './blogPostsData';

const BlogPosts: React.FC = () => {
  const POSTS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  // Sort blog posts by date (newest first)
  const sortedPosts = useMemo(() => {
    return [...blogPosts].sort((a, b) => {
      // Parse dates from format "DD MMM YYYY" (e.g., "21 Jan 2026")
      const parseDate = (dateStr: string): Date => {
        const months: { [key: string]: number } = {
          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        const parts = dateStr.split(' ');
        const day = parseInt(parts[0]);
        const month = months[parts[1]];
        const year = parseInt(parts[2]);
        return new Date(year, month, day);
      };
      
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });
  }, []);

  const totalPages = Math.ceil(sortedPosts.length / POSTS_PER_PAGE);

  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    return sortedPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [currentPage, sortedPosts]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages || 1));
  }, [totalPages]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="bg-[#F3F4F6] py-16 md:py-20">
      <div className="mx-auto px-4 md:px-10 lg:px-32 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {paginatedPosts.map((post) => (
            <BlogPostCard
              key={post.id}
              image={post.image}
              title={post.title}
              description={post.description}
              author={post.author}
              authorImage={post.authorImage}
              date={post.date}
              link={post.link}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-5 text-[20px] md:text-[22px] leading-none">
            {currentPage > 1 && (
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                className="mr-6 text-[20px] md:text-[22px] font-[400] text-[#000] hover:text-[#4B5563] transition-colors"
                aria-label="Go to previous page"
              >
                previous page
              </button>
            )}

            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => goToPage(page)}
                className={`transition-colors ${currentPage === page ? 'font-[700] text-[#000]' : 'font-[400] text-[#000] hover:text-[#4B5563]'}`}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            ))}

            {currentPage < totalPages && (
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                className="ml-6 text-[20px] md:text-[22px] font-[400] text-[#000] hover:text-[#4B5563] transition-colors"
                aria-label="Go to next page"
              >
                next page
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogPosts;
